import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { IIncidentQuestionsConfig, IIncidentReport } from '../models/incident.model';
import { GlobalConfigService } from './global-config.service';

@Injectable({
  providedIn: 'root'
})
export class IncidentService {
  private get baseUrl(): string {
    return this.globalConfig.getApiUrl();
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) {}

  /**
   * Busca a configuração de perguntas de intercorrência da clínica
   */
  getClinicQuestions(): Promise<IIncidentQuestionsConfig> {
    return new Promise((resolve, reject) => {
      this.http.get<IIncidentQuestionsConfig>(`${this.baseUrl}/incident-questions`)
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Busca as intercorrências registradas de um plano de tratamento.
   *
   * Backend expõe um único relatório por plano em GET /treatment-plans/{id}/incident-report.
   * Para compatibilidade com chamadores que esperam array, devolve [] em 404 e [report]
   * quando existe.
   *
   * Timeout/retry: 8s + 2 retentativas em erros transientes (status 0 / TimeoutError),
   * mesmo padrão do TreatmentPlanService — evita travar a tela quando o backend
   * está lento e protege contra picos de latência intermitente.
   */
  getIncidentReports(treatmentPlanId: string): Promise<IIncidentReport[]> {
    return new Promise((resolve, reject) => {
      this.http.get<IIncidentReport>(`${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report`)
        .pipe(
          timeout(8000),
          retry({
            count: 2,
            delay: (error, retryCount) => {
              const isTransient = error?.status === 0 || error?.name === 'TimeoutError';
              if (!isTransient) {
                return throwError(() => error);
              }
              const backoffMs = retryCount === 1 ? 500 : 1500;
              return timer(backoffMs);
            }
          })
        )
        .subscribe({
          next: (response) => resolve(response ? [response] : []),
          error: (error) => {
            if (error && error.status === 404) {
              resolve([]);
            } else {
              reject(error);
            }
          }
        });
    });
  }

  /**
   * Salva um novo relatório de intercorrência
   */
  saveIncidentReport(treatmentPlanId: string, report: Omit<IIncidentReport, 'id' | 'created_at' | 'updated_at'>): Promise<IIncidentReport> {
    return new Promise((resolve, reject) => {
      this.http.post<IIncidentReport>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report`,
        report
      ).subscribe({
        next: (response) => resolve(response),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Atualiza um relatório de intercorrência existente
   */
  updateIncidentReport(treatmentPlanId: string, reportId: string, report: Partial<IIncidentReport>): Promise<IIncidentReport> {
    return new Promise((resolve, reject) => {
      this.http.put<IIncidentReport>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report`,
        report
      ).subscribe({
        next: (response) => resolve(response),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Finaliza um relatório de intercorrência (status → CLOSED).
   * O PDF é gerado no backend via Thymeleaf + openhtmltopdf — o frontend
   * apenas dispara o endpoint sem enviar arquivo.
   * Ação irreversível — bloqueia edições posteriores.
   *
   * @param treatmentPlanId ID do plano de tratamento
   * @returns Relatório atualizado com autentique_signing_link
   */
  finalizeIncidentReport(treatmentPlanId: string): Promise<IIncidentReport> {
    return new Promise((resolve, reject) => {
      this.http.patch<IIncidentReport>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report/finalize`,
        {}
      ).subscribe({
        next: (response) => resolve(response),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Baixa o PDF do relatório de intercorrência gerado no backend.
   * Funciona para qualquer intercorrência existente (não precisa estar CLOSED).
   *
   * @param treatmentPlanId ID do plano de tratamento
   * @returns Blob do PDF
   */
  downloadIncidentPdf(treatmentPlanId: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http.get(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report/pdf`,
        { responseType: 'blob' }
      ).subscribe({
        next: (blob) => resolve(blob),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Remove um relatório de intercorrência
   */
  deleteIncidentReport(treatmentPlanId: string, reportId: string): Promise<void> {
    void reportId;

    return new Promise((resolve, reject) => {
      this.http.delete<void>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report`
      ).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Busca as seções de intercorrência (GET /incidents/sections)
   * Retorna um array: [{id, label}, ...]
   */
  getGlobalQuestions(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.baseUrl}/incidents/sections`)
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Busca a versão mais recente publicada das perguntas de intercorrência
   */
  getLatestQuestions(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get<any>(`${this.baseUrl}/incidents/questions/latest`)
        .subscribe({
          next: (response) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Publica a configuração global de perguntas de intercorrência
   */
  publishQuestions(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post<any>(
        `${this.baseUrl}/incident-questions/global/publish`,
        payload
      ).subscribe({
        next: (response) => resolve(response),
        error: (error) => reject(error)
      });
    });
  }

  /**
   * Remove uma pergunta global de intercorrência
   * DELETE /incident-questions/global/{questionId}
   */
  deleteGlobalQuestion(questionId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.delete<void>(
        `${this.baseUrl}/incident-questions/global/${questionId}`
      ).subscribe({
        next: () => resolve(),
        error: (error) => reject(error)
      });
    });
  }
}
